'use client';

import React from 'react';
import { useParams } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faBookmark } from '@fortawesome/pro-solid-svg-icons';
import { LOCATION } from 'src/hooks/filters/constants';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import { useGetProviderVolumeQuery } from 'src/api/redux';
import { CircularProgress } from 'src/components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { resolveClaimsMetric } from 'src/utils/encounterLabels';
import {
  PROFILE_VOLUME_MIN_YEAR,
  PROFILE_VOLUME_MAX_YEAR,
} from 'src/components/constants';

const CodeSetBlock = () => {
  const { id } = useParams<{ id: string }>();

  const { selectedFilterSet, selectedCohort } = useProfileSelections(
    LOCATION.SYSTEM
  );
  const { showEncountersTotals } = useFlags();

  const {
    data: systemVolume,
    isLoading,
    isFetching,
  } = useGetProviderVolumeQuery(
    {
      providerId: id,
      filter_set: selectedFilterSet?.id || null,
      cohort_id: selectedCohort?.id || null,
      dateRange: { gte: PROFILE_VOLUME_MIN_YEAR, lte: PROFILE_VOLUME_MAX_YEAR },
    },
    { skip: !id }
  );

  const hasSelectedValue = selectedCohort?.label || selectedFilterSet?.label;
  const label = selectedCohort?.label || selectedFilterSet?.label;

  // For a cohort selection, show encounters — value and label together, so a payload
  // with no `encounters` field keeps the "Claims" label over its claims number.
  const { value: claimsValue, label: claimsLabel } = resolveClaimsMetric(
    systemVolume,
    !!selectedCohort?.id,
    !!showEncountersTotals
  );

  return (
    <div
      data-testid="code-set-block"
      className={`relative self-stretch rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)] shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.00)] outline outline-1 -outline-offset-1 inline-flex flex-col justify-start items-start gap-2 overflow-hidden ${
        hasSelectedValue
          ? 'bg-teal-50 outline-teal-700'
          : 'bg-gray-50 outline-gray-300'
      }`}
    >
      <div className="self-stretch h-28 px-5 py-4 flex flex-col justify-between items-start gap-2">
        <div className="self-stretch inline-flex justify-start items-center gap-1">
          <div className="w-5 h-5 flex items-center justify-center">
            <Icon
              icon={faBookmark}
              className={`w-5 h-5 ${
                hasSelectedValue ? 'text-teal-700' : 'text-gray-400'
              }`}
              data-testid="code-set-icon"
            />
          </div>
          <div
            data-testid="code-set-label"
            className={`flex-1 justify-start text-sm font-bold font-['Inter'] leading-tight truncate ${
              hasSelectedValue ? 'text-teal-700' : 'text-gray-500'
            }`}
          >
            {label || 'Select a code set'}
          </div>
        </div>
        {hasSelectedValue ? (
          <div
            data-testid="volume-data"
            className="self-stretch inline-flex justify-between items-end"
          >
            <div className="inline-flex flex-col justify-end items-start gap-0.5">
              <div className="justify-start text-teal-900 text-xs font-normal font-['Inter'] leading-none">
                {`${PROFILE_VOLUME_MAX_YEAR} ${claimsLabel}`}
              </div>
              <div
                data-testid="claims-value"
                className="justify-start text-teal-800 text-3xl font-extrabold font-['Inter'] leading-9"
              >
                {claimsValue
                  ? isNaN(Number(claimsValue))
                    ? String(claimsValue)
                    : Number(claimsValue).toLocaleString()
                  : 0}
              </div>
            </div>
            <div className="inline-flex flex-col justify-end items-start gap-0.5">
              <div className="justify-start text-teal-900 text-xs font-normal font-['Inter'] leading-none">
                {`${PROFILE_VOLUME_MAX_YEAR} Patients`}
              </div>
              <div
                data-testid="patients-value"
                className="justify-start text-teal-800 text-3xl font-extrabold font-['Inter'] leading-9"
              >
                {systemVolume?.patients
                  ? isNaN(Number(systemVolume.patients))
                    ? String(systemVolume.patients)
                    : Number(systemVolume.patients).toLocaleString()
                  : 0}
              </div>
            </div>
          </div>
        ) : (
          <div className="self-stretch flex items-end">
            <div
              data-testid="empty-state-message"
              className="text-gray-500 text-sm font-normal font-['Inter'] leading-tight"
            >
              Select a code set to view claims and patient data
            </div>
          </div>
        )}
      </div>
      {(isLoading || isFetching) && hasSelectedValue && (
        <div
          data-testid="loading-overlay"
          className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center rounded-lg z-10"
        >
          <CircularProgress size="sm" color="neutral" />
        </div>
      )}
    </div>
  );
};

export default CodeSetBlock;
